import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  CreditScore,
  Assessment,
  ExpandMore,
  ExpandLess,
  Download,
  Refresh,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  AccountBalanceWallet,
  Receipt,
  Timeline,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { fetchDataEngine } from "../../../data/Axios/DynamicService";
import { reportManagementUrl } from "../../../config";
import { generateReport } from "../../../data/DynamicTable/DynamicFunctions";
import { allBankStatements } from "../../../data/Axios/queries";
import Swal from "sweetalert2";
import PropTypes from "prop-types";

const base_url = reportManagementUrl.uri;

// ============== CREDIT SCORE GAUGE COMPONENT ==============
const CreditScoreGauge = ({ score, maxScore = 1000, size = 200 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Calculate the arc length - the arc spans 240 degrees (from -210 to 30 degrees)
  const percentage = Math.min((score || 0) / maxScore, 1);
  const arcLength = 240; // degrees
  const filledArc = percentage * arcLength;

  const getScoreColor = (score) => {
    if (score >= 800) return "#00C853";
    if (score >= 700) return "#64DD17";
    if (score >= 600) return "#FFD600";
    if (score >= 500) return "#FF9100";
    return "#D50000";
  };

  const getScoreLabel = (score) => {
    if (!score || score === 0) return "No Data";
    if (score >= 800) return "Excellent";
    if (score >= 700) return "Good";
    if (score >= 600) return "Fair";
    if (score >= 500) return "Poor";
    return "Very Poor";
  };

  const scoreColor = getScoreColor(score);

  // Create arc path using polar coordinates
  const createArc = (startAngle, endAngle, radius = 80) => {
    const start = {
      x: 100 + radius * Math.cos((startAngle * Math.PI) / 180),
      y: 100 + radius * Math.sin((startAngle * Math.PI) / 180),
    };
    const end = {
      x: 100 + radius * Math.cos((endAngle * Math.PI) / 180),
      y: 100 + radius * Math.sin((endAngle * Math.PI) / 180),
    };
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  };

  // Background arc: from -210° to 30° (240° total arc)
  const bgArcPath = createArc(-210, 30);
  // Filled arc: from -210° to (-210 + filledArc)°
  const filledArcPath = createArc(-210, -210 + filledArc);

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
      }}
    >
      <svg width={size} height={size * 0.65} viewBox="0 0 200 130">
        {/* Background arc */}
        <path
          d={bgArcPath}
          fill="none"
          stroke={
            theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[300]
          }
          strokeWidth="14"
          strokeLinecap="round"
        />
        {/* Score arc */}
        {score > 0 && (
          <path
            d={filledArcPath}
            fill="none"
            stroke={scoreColor}
            strokeWidth="14"
            strokeLinecap="round"
            style={{
              transition: "all 1s ease-in-out",
              filter: `drop-shadow(0 0 6px ${scoreColor}50)`,
            }}
          />
        )}
        {/* Score text */}
        <text
          x="100"
          y="85"
          textAnchor="middle"
          fill={scoreColor}
          fontSize="32"
          fontWeight="bold"
        >
          {score ? score.toFixed(2) : "N/A"}
        </text>
        <text
          x="100"
          y="110"
          textAnchor="middle"
          fill={
            theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[600]
          }
          fontSize="12"
        >
          {getScoreLabel(score)}
        </text>
      </svg>
    </Box>
  );
};

CreditScoreGauge.propTypes = {
  score: PropTypes.number,
  maxScore: PropTypes.number,
  size: PropTypes.number,
};

// ============== METRIC CARD COMPONENT ==============
const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Card
      sx={{
        height: "100%",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
            : `linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)`,
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]
        }`,
        borderRadius: "16px",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 12px 28px ${colors.primary[900]}60`
              : "0 12px 28px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: `${color}25`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: color, fontSize: 28 }} />
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={trend > 0 ? <TrendingUp /> : <TrendingDown />}
              label={`${trend > 0 ? "+" : ""}${trend}%`}
              sx={{
                backgroundColor: trend > 0 ? "#00C85320" : "#D5000020",
                color: trend > 0 ? "#00C853" : "#D50000",
                "& .MuiChip-icon": {
                  color: trend > 0 ? "#00C853" : "#D50000",
                },
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            mb: 0.5,
            color:
              theme.palette.mode === "dark"
                ? colors.grey[300]
                : colors.grey[600],
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            color: color || "inherit",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          {value || "N/A"}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[500],
              mt: 0.5,
              display: "block",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
  trend: PropTypes.number,
  subtitle: PropTypes.string,
};

// ============== RISK BADGE COMPONENT ==============
const RiskBadge = ({ category }) => {
  const getConfig = (cat) => {
    const lower = (cat || "").toLowerCase();
    if (
      lower.includes("low") ||
      lower.includes("excellent") ||
      lower.includes("minimal")
    ) {
      return {
        color: "#00C853",
        bg: "#00C85330",
        icon: CheckCircle,
        label: "Low Risk",
      };
    }
    if (
      lower.includes("medium") ||
      lower.includes("fair") ||
      lower.includes("moderate")
    ) {
      return {
        color: "#FFD600",
        bg: "#FFD60030",
        icon: Warning,
        label: "Medium Risk",
      };
    }
    if (lower.includes("high") && !lower.includes("very")) {
      return {
        color: "#FF9100",
        bg: "#FF910030",
        icon: Warning,
        label: "High Risk",
      };
    }
    return {
      color: "#D50000",
      bg: "#D5000030",
      icon: ErrorIcon,
      label: "Very High Risk",
    };
  };

  const config = getConfig(category);
  const Icon = config.icon;

  return (
    <Chip
      icon={<Icon sx={{ color: `${config.color} !important`, fontSize: 20 }} />}
      label={category || "Unknown"}
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: "bold",
        fontSize: "0.875rem",
        py: 2.5,
        px: 1.5,
        borderRadius: "8px",
        border: `1px solid ${config.color}40`,
        boxShadow: `0 2px 8px ${config.color}30`,
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
};

RiskBadge.propTypes = {
  category: PropTypes.string,
};

// ============== ENHANCED DATA TABLE COMPONENT ==============
const EnhancedDataTable = ({ data, summary, title }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [expanded, setExpanded] = useState(true);
  const [orderBy, setOrderBy] = useState("Total");
  const [order, setOrder] = useState("desc");

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      const aVal = a[orderBy] || 0;
      const bVal = b[orderBy] || 0;
      return order === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [data, orderBy, order]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "N/A";
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[300]
        }`,
        overflow: "hidden",
        backgroundColor:
          theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.primary[500]
              : colors.grey[100],
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt sx={{ color: colors.greenAccent[500] }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              color: theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
            }}
          >
            {title}
          </Typography>
          <Chip
            size="small"
            label={`${data?.length || 0} records`}
            sx={{
              ml: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.primary[400]
                  : colors.grey[200],
              color: theme.palette.mode === "dark" ? "#fff" : colors.grey[700],
            }}
          />
        </Box>
        <IconButton
          size="small"
          sx={{
            color: theme.palette.mode === "dark" ? "#fff" : colors.grey[700],
          }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Summary Section */}
        {summary && Object.keys(summary).length > 0 && (
          <Box
            sx={{
              p: 2,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.primary[600]
                  : colors.grey[50],
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color:
                  theme.palette.mode === "dark"
                    ? colors.grey[300]
                    : colors.grey[600],
              }}
            >
              Summary Metrics
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(summary)
                .slice(0, 8)
                .map(([key, value]) => (
                  <Grid item xs={6} sm={3} key={key}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? colors.grey[400]
                              : colors.grey[600],
                        }}
                      >
                        {key.replace(/([A-Z])/g, " $1").trim()}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#fff"
                              : colors.grey[800],
                        }}
                      >
                        {typeof value === "number"
                          ? formatCurrency(value)
                          : value || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        {/* Data Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? colors.primary[500]
                      : colors.grey[200],
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  Account
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  Names
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Count"}
                    direction={orderBy === "Count" ? order : "asc"}
                    onClick={() => handleSort("Count")}
                  >
                    Count
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Highest"}
                    direction={orderBy === "Highest" ? order : "asc"}
                    onClick={() => handleSort("Highest")}
                  >
                    Highest
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark" ? "#fff" : colors.grey[800],
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Total"}
                    direction={orderBy === "Total" ? order : "asc"}
                    onClick={() => handleSort("Total")}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length > 0 ? (
                sortedData.map((transaction, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? colors.primary[400]
                          : "#fff",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? colors.primary[500]
                            : colors.grey[100],
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "#fff"
                            : colors.grey[800],
                      }}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#fff"
                              : colors.grey[800],
                        }}
                      >
                        {transaction.Account}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "#fff"
                            : colors.grey[800],
                      }}
                    >
                      {transaction.Names}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={transaction.Count || 0}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? colors.primary[500]
                              : colors.grey[200],
                          color:
                            theme.palette.mode === "dark"
                              ? "#fff"
                              : colors.grey[800],
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "#fff"
                            : colors.grey[800],
                      }}
                    >
                      {formatCurrency(transaction.Highest)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight="bold"
                        color={colors.greenAccent[500]}
                      >
                        {formatCurrency(transaction.Total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      py: 4,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? colors.primary[400]
                          : "#fff",
                    }}
                  >
                    <Info
                      sx={{ fontSize: 48, color: colors.grey[500], mb: 1 }}
                    />
                    <Typography color="text.secondary">
                      No transaction data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Card>
  );
};

EnhancedDataTable.propTypes = {
  data: PropTypes.array,
  summary: PropTypes.object,
  title: PropTypes.string.isRequired,
};

// ============== SUMMARY TABLE FOR M-PESA ==============
const EnhancedSummaryTable = ({ data }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "Ksh 0";
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    })
      .format(value)
      .replace("KES", "Ksh");
  };
  const filteredData =
    data?.filter(
      (item) => !item.transactionType?.toUpperCase().includes("TOTAL")
    ) || [];
  // Calculate totals
  const totals = filteredData?.reduce(
    (acc, item) => ({
      paidIn: acc.paidIn + (item.paidIn || 0),
      paidOut: acc.paidOut + (item.paidOut || 0),
    }),
    { paidIn: 0, paidOut: 0 }
  ) || { paidIn: 0, paidOut: 0 };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]
        }`,
      }}
    >
      <Table size="medium">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.primary[500]
                  : colors.grey[100],
            }}
          >
            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
              Transaction Type
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
              align="right"
            >
              Paid In
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
              align="right"
            >
              Paid Out
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData && filteredData.length > 0 ? (
            <>
              {filteredData.map((item, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? colors.primary[600]
                          : colors.grey[50],
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    {item.transactionType}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        color: "#00C853",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(item.paidIn)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        color: "#D50000",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(item.paidOut)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? colors.primary[400]
                      : colors.grey[100],
                  "& td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                  TOTAL:
                </TableCell>
                <TableCell align="right">
                  <Typography
                    sx={{
                      color: "#00C853",
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                    }}
                  >
                    {formatCurrency(totals.paidIn)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    sx={{
                      color: "#D50000",
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                    }}
                  >
                    {formatCurrency(totals.paidOut)}
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">
                  No summary data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

EnhancedSummaryTable.propTypes = {
  data: PropTypes.array,
};

// ============== LOADING SKELETON ==============
const LoadingSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item}>
            <Skeleton
              variant="rounded"
              height={140}
              sx={{ borderRadius: "12px" }}
            />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3 }}>
        <Skeleton
          variant="rounded"
          height={300}
          sx={{ borderRadius: "12px" }}
        />
      </Box>
    </Box>
  );
};

// ============== TAB PANEL ==============
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credit-tabpanel-${index}`}
      aria-labelledby={`credit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// ============== MAIN COMPONENT ==============
function EnhancedCreditReport({ reportType = "mpesa" }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [insightTypes, setInsightTypes] = useState([]);
  const [statementData, setStatementData] = useState({});
  const [creditScore, setCreditScore] = useState({});
  const [opoData, setOpoData] = useState({});
  const [cleanedSummary, setCleanedSummary] = useState([]);
  const [transactionTypesSummary, setTransactionTypesSummary] = useState([]);
  const [insightTypesSummary, setInsightTypesSummary] = useState([]);

  const statementInfo = JSON.parse(
    localStorage.getItem("statementData") || "{}"
  );
  const isBank = reportType === "bank";

  // Fetch statement data
  useEffect(() => {
    async function fetchStatementData() {
      try {
        setIsLoading(true);
        setError(null);

        if (isBank) {
          // Bank statement fetch logic
          const token = localStorage.getItem("token");
          const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

          const response = await fetch(base_url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: formattedToken,
            },
            body: JSON.stringify({ query: allBankStatements }),
          });

          const jsonResponse = await response.json();
          const facts = jsonResponse.data?.bankStatementsView;

          if (facts && facts[0]) {
            setStatementData(facts[0]);
            if (facts[0].dynamicIdentifier) {
              try {
                setCleanedSummary(JSON.parse(facts[0].dynamicIdentifier));
              } catch (e) {
                console.error("Error parsing dynamicIdentifier:", e);
              }
            }
          }
        } else {
          // M-Pesa statement fetch logic
          const returnFields = [
            "id",
            "userId",
            "speedPlanId",
            "customerName",
            "customerMobile",
            "emailAddress",
            "statementPeriod",
            "requestDate",
            "summary",
            "createdDate",
            "totalWork",
            "workDone",
            "isPaid",
          ];
          const inputFields = { id: statementInfo.id };

          const Facts = await fetchDataEngine(
            "statementById",
            "statement",
            inputFields,
            returnFields,
            base_url
          );

          setStatementData(Facts);

          if (Facts?.summary) {
            try {
              const parsed = JSON.parse(Facts.summary).map((item) => ({
                transactionType: item.TransactionType || "Unknown",
                paidIn: item.PaidIn ?? 0,
                paidOut: item.PaidOut ?? 0,
              }));
              setCleanedSummary(parsed);
            } catch (e) {
              console.error("Error parsing summary:", e);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching statement data:", err);
        setError("Failed to load statement data");
      } finally {
        setIsLoading(false);
      }
    }

    fetchStatementData();
  }, [isBank, statementInfo.id]);

  // Fetch credit score and OPO data
  useEffect(() => {
    async function fetchCreditData() {
      try {
        const creditReturnFields = [
          "creditscore",
          "totalScore",
          "riskCategory",
          "loanableMonthly",
          "loanableWeekly",
        ];
        const opoReturnFields = [
          "binnedScore",
          "creditScore",
          "riskCategory",
          "activeLoansCount",
          "activeLoansScore",
          "binnedScoreAverage",
          "nonPerformingLoansCount",
          "nonPerformingLoansScore",
        ];

        const [creditData, opoResult] = await Promise.all([
          fetchDataEngine(
            "creditScore",
            "functionParams",
            { pStatementId: statementInfo.id },
            creditReturnFields,
            base_url
          ),
          fetchDataEngine(
            "opoData",
            "functionParams",
            { paramstatementid: statementInfo.id },
            opoReturnFields,
            base_url
          ),
        ]);

        setCreditScore(creditData || {});
        setOpoData(opoResult || {});
      } catch (err) {
        console.error("Error fetching credit data:", err);
      }
    }

    if (statementInfo.id) {
      fetchCreditData();
    }
  }, [statementInfo.id]);

  // Fetch transaction data
  useEffect(() => {
    async function fetchTransactionData() {
      try {
        const inputFields = { p_StatementId: statementInfo.id };
        const returnFields = ["category", "name", "transactionsData"];

        const endpoint = isBank
          ? "bankCombinedFrequentData"
          : "combinedFrequentData";
        const transactions = await fetchDataEngine(
          endpoint,
          "functionParams",
          inputFields,
          returnFields,
          base_url
        );

        const cleanedData = (transactions || []).map((item) => {
          let cleanedTransactions = [];
          try {
            const transactionsList = JSON.parse(item.transactionsData || "[]");
            cleanedTransactions = transactionsList.map((t) => ({
              ...t,
              Names: t.Names || "N/A",
              Account: t.Account || "N/A",
              Total: t.Total ?? 0,
              Highest: t.Highest ?? 0,
            }));
          } catch (e) {
            console.error("Error parsing transactions:", e);
          }
          return {
            category: item.category,
            name: item.name,
            transactionsData: cleanedTransactions,
          };
        });

        setTransactionTypes(
          cleanedData.filter((i) => i.category === "TransactionType")
        );
        setInsightTypes(cleanedData.filter((i) => i.category === "Insight"));
      } catch (err) {
        console.error("Error fetching transaction data:", err);
      }
    }

    if (statementInfo.id) {
      fetchTransactionData();
    }
  }, [statementInfo.id, isBank]);

  // Fetch summary data
  useEffect(() => {
    async function fetchSummaryData() {
      try {
        const inputFields = { statementId: statementInfo.id };
        const returnFields = ["category", "name", "summary"];

        const endpoint = isBank ? "bankCombinedSummary" : "combinedSummary";
        const data = await fetchDataEngine(
          endpoint,
          "functionParams",
          inputFields,
          returnFields,
          base_url
        );

        const cleanedData = (data || []).map((item) => {
          let cleaned = {};
          try {
            if (item.summary) {
              cleaned = JSON.parse(item.summary);
            }
          } catch (e) {
            console.error("Error parsing summary:", e);
          }
          return {
            category: item.category,
            name: item.name,
            summary: cleaned,
          };
        });

        setTransactionTypesSummary(
          cleanedData.filter(
            (i) => i.category?.toLowerCase() === "transactiontype"
          )
        );
        setInsightTypesSummary(
          cleanedData.filter((i) => i.category?.toLowerCase() === "insight")
        );
      } catch (err) {
        console.error("Error fetching summary data:", err);
      }
    }

    if (statementInfo.id) {
      fetchSummaryData();
    }
  }, [statementInfo.id, isBank]);

  // Generate report handler
  const handleGenerateReport = async () => {
    try {
      setIsLoading(true);
      const newTab = window.open("", "_blank");

      newTab.document.write(`
        <html>
          <head>
            <title>Generating Report...</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
              }
              .loader {
                width: 48px;
                height: 48px;
                border: 4px solid #ffffff40;
                border-top: 4px solid white;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
              .text { margin-top: 20px; font-size: 18px; }
            </style>
          </head>
          <body>
            <div class="loader"></div>
            <div class="text">Generating your report...</div>
          </body>
        </html>
      `);
      newTab.document.close();

      const response = await generateReport(
        { StatementId: statementInfo.id },
        reportManagementUrl.fs2_engine_uri,
        setIsLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        newTab.close();
        throw new Error(response.statusText);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Report Generation Failed",
        text: "An error occurred while generating the report. Please try again.",
        confirmButtonColor: colors.greenAccent[500],
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Combine transaction data with summaries
  const combinedTransactions = transactionTypes.map((item) => {
    const summaryItem = transactionTypesSummary.find(
      (s) => s.name === item.name
    );
    return {
      name: item.name,
      data: item.transactionsData,
      summary: summaryItem?.summary || {},
    };
  });

  const combinedInsights = insightTypes.map((item) => {
    const summaryItem = insightTypesSummary.find((s) => s.name === item.name);
    return {
      name: item.name,
      data: item.transactionsData,
      summary: summaryItem?.summary || {},
    };
  });

  if (isLoading && !creditScore.creditscore) {
    return (
      <Box sx={{ p: 3 }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor:
          theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[50],
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {isBank ? "Bank Statement" : "M-Pesa Statement"} Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive credit scoring and transaction insights
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => window.location.reload()}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Download />
              )
            }
            onClick={handleGenerateReport}
            disabled={isLoading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              "&:hover": { backgroundColor: colors.greenAccent[600] },
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
            }}
          >
            {isLoading ? "Generating..." : "Generate Report"}
          </Button>
        </Box>
      </Box>

      {/* Credit Score Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: "16px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? colors.primary[300]
                  : colors.grey[200]
              }`,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                  : `linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 4px 20px ${colors.primary[900]}40`
                  : "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color:
                  theme.palette.mode === "dark"
                    ? colors.grey[200]
                    : colors.grey[700],
              }}
            >
              Credit Score
            </Typography>
            <CreditScoreGauge score={creditScore.creditscore || 0} />
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <RiskBadge category={creditScore.riskCategory} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Total Score"
                value={creditScore.totalScore || "N/A"}
                icon={Assessment}
                color={colors.blueAccent[500]}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Loanable Monthly"
                value={
                  creditScore.loanableMonthly
                    ? `KES ${Number(
                        creditScore.loanableMonthly
                      ).toLocaleString()}`
                    : "N/A"
                }
                icon={AccountBalanceWallet}
                color={colors.greenAccent[500]}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Loanable Weekly"
                value={
                  creditScore.loanableWeekly
                    ? `KES ${Number(
                        creditScore.loanableWeekly
                      ).toLocaleString()}`
                    : "N/A"
                }
                icon={Timeline}
                color={
                  colors.purpleAccent ? colors.purpleAccent[500] : "#9c27b0"
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Active Loans"
                value={opoData.activeLoansCount || 0}
                icon={CreditScore}
                color="#FF9100"
                subtitle={`Score: ${opoData.activeLoansScore || "N/A"}`}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Customer Details & OPO Data */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: "16px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? colors.primary[300]
                  : colors.grey[200]
              }`,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                  : `linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 4px 20px ${colors.primary[900]}40`
                  : "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: `${colors.greenAccent[500]}25`,
                  }}
                >
                  <AccountBalance sx={{ color: colors.greenAccent[500] }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {isBank ? "Account Details" : "Customer Details"}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />

              {isBank ? (
                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Account Name
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      sx={{ mt: 0.5 }}
                    >
                      {cleanedSummary?.["Account Name"] || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Account Number
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      sx={{ fontFamily: "monospace", mt: 0.5 }}
                    >
                      {cleanedSummary?.["Account"] || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Available Balance
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={colors.greenAccent[500]}
                      sx={{ mt: 0.5 }}
                    >
                      {cleanedSummary?.["Available Balance"] || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Period
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {cleanedSummary?.["Period"] || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Bank Name
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {statementData.bankName || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Customer Name
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                      {statementData.customerName || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {statementData.emailAddress || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {statementData.customerMobile || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[600],
                      }}
                    >
                      Statement Period
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {statementData.statementPeriod || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: "16px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? colors.primary[300]
                  : colors.grey[200]
              }`,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                  : `linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 4px 20px ${colors.primary[900]}40`
                  : "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: `${colors.greenAccent[500]}25`,
                  }}
                >
                  <CreditScore sx={{ color: colors.greenAccent[500] }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Sababisha Analysis
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? colors.grey[400]
                          : colors.grey[600],
                    }}
                  >
                    Binned Score
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mt: 0.5 }}
                  >
                    {opoData.binnedScore ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? colors.grey[400]
                          : colors.grey[600],
                    }}
                  >
                    Score Average
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mt: 0.5 }}
                  >
                    {opoData.binnedScoreAverage ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? colors.grey[400]
                          : colors.grey[600],
                    }}
                  >
                    NPL Count
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#D50000"
                    fontWeight="bold"
                    sx={{ mt: 0.5 }}
                  >
                    {opoData.nonPerformingLoansCount ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? colors.grey[400]
                          : colors.grey[600],
                    }}
                  >
                    NPL Score
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {opoData.nonPerformingLoansScore ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? colors.grey[400]
                          : colors.grey[600],
                    }}
                  >
                    Risk Category
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <RiskBadge category={opoData.riskCategory} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Table for M-Pesa */}
      {!isBank && cleanedSummary.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: "12px", overflow: "hidden" }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Transaction Summary
            </Typography>
            <EnhancedSummaryTable data={cleanedSummary} />
          </CardContent>
        </Card>
      )}

      {/* Tabs for Transactions and Insights */}
      <Card sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[500]
                : colors.grey[100],
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 56,
            },
            "& .Mui-selected": {
              color: `${colors.greenAccent[500]} !important`,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: colors.greenAccent[500],
              height: 3,
            },
          }}
        >
          <Tab
            icon={<Receipt sx={{ mr: 1 }} />}
            iconPosition="start"
            label={`Transactions (${transactionTypes.length})`}
          />
          <Tab
            icon={<Timeline sx={{ mr: 1 }} />}
            iconPosition="start"
            label={`Insights (${insightTypes.length})`}
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TabPanel value={tabIndex} index={0}>
            {combinedTransactions.length > 0 ? (
              combinedTransactions.map((item, idx) => (
                <EnhancedDataTable
                  key={idx}
                  title={item.name}
                  data={item.data}
                  summary={item.summary}
                />
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Info sx={{ fontSize: 64, color: colors.grey[500], mb: 2 }} />
                <Typography color="text.secondary">
                  No transaction data available
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            {combinedInsights.length > 0 ? (
              combinedInsights.map((item, idx) => (
                <EnhancedDataTable
                  key={idx}
                  title={item.name}
                  data={item.data}
                  summary={item.summary}
                />
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Info sx={{ fontSize: 64, color: colors.grey[500], mb: 2 }} />
                <Typography color="text.secondary">
                  No insight data available
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Box>
      </Card>
    </Box>
  );
}

EnhancedCreditReport.propTypes = {
  reportType: PropTypes.oneOf(["mpesa", "bank"]),
};

export default EnhancedCreditReport;
